import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { createOrder, captureOrder, getPurchaseStatus, getPrice } from '../../api/client';
import { Analytics, trackMetaEvent } from '../../utils/analytics';

/**
 * Generate an event ID for Meta Pixel/CAPI deduplication
 */
const generateEventId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// PayPal configuration - auto-select based on mode
const PAYPAL_MODE = process.env.REACT_APP_PAYPAL_MODE || 'sandbox';
const PAYPAL_SANDBOX_CLIENT_ID = process.env.REACT_APP_PAYPAL_SANDBOX_CLIENT_ID;
const PAYPAL_LIVE_CLIENT_ID = process.env.REACT_APP_PAYPAL_LIVE_CLIENT_ID;

// Legacy support: fall back to single client ID if new vars aren't set
const PAYPAL_CLIENT_ID = PAYPAL_MODE === 'live' 
  ? (PAYPAL_LIVE_CLIENT_ID || process.env.REACT_APP_PAYPAL_CLIENT_ID)
  : (PAYPAL_SANDBOX_CLIENT_ID || process.env.REACT_APP_PAYPAL_CLIENT_ID);

/**
 * PayPal checkout modal for purchasing the full digital book.
 */
export default function PayPalCheckout({
  isOpen,
  onClose,
  onPurchaseComplete,
  sessionId,
  bookTitle,
  totalChapters,
}) {
  const [status, setStatus] = useState('idle'); // idle, loading, ready, processing, success, error
  const [error, setError] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);

  // Fetch price info on mount
  useEffect(() => {
    if (isOpen && !priceInfo) {
      setStatus('loading');
      getPrice()
        .then((data) => {
          setPriceInfo(data);
          setStatus('ready');
        })
        .catch((err) => {
          console.error('Failed to get price:', err);
          setError('Failed to load pricing. Please try again.');
          setStatus('error');
        });
    }
  }, [isOpen, priceInfo]);

  // Check if already purchased
  useEffect(() => {
    if (isOpen && sessionId) {
      getPurchaseStatus(sessionId)
        .then((data) => {
          if (data.purchased) {
            setStatus('success');
          }
        })
        .catch((err) => {
          console.error('Failed to check purchase status:', err);
        });
    }
  }, [isOpen, sessionId]);

  if (!isOpen) return null;

  // If PayPal is not configured, show error
  if (!PAYPAL_CLIENT_ID) {
    return (
      <ModalWrapper onClose={onClose}>
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-rust/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-rust" />
          </div>
          <h3 className="font-display text-2xl font-bold text-ink mb-2">
            Payment Unavailable
          </h3>
          <p className="text-sepia mb-6">
            Payment processing is not configured. Please contact support.
          </p>
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </ModalWrapper>
    );
  }

  const handleCreateOrder = async () => {
    try {
      const response = await createOrder(sessionId, bookTitle);
      return response.order_id;
    } catch (err) {
      console.error('Create order error:', err);
      throw err;
    }
  };

  const handleApprove = async (data) => {
    setStatus('processing');
    try {
      // Generate event ID for Meta Pixel/CAPI deduplication
      const eventId = generateEventId();
      
      // Pass event ID to backend for CAPI
      const response = await captureOrder(sessionId, data.orderID, eventId);
      
      if (response.status === 'COMPLETED') {
        setStatus('success');
        // Track GA4 purchase
        Analytics.trackPurchaseCompleted(priceInfo?.price, priceInfo?.currency);
        // Track Meta Pixel purchase with same event ID for deduplication
        trackMetaEvent('Purchase', {
          value: parseFloat(priceInfo?.price || 0),
          currency: priceInfo?.currency || 'USD',
          content_type: 'product',
          content_name: 'Digital Yearbook',
        }, eventId);
        if (onPurchaseComplete) {
          onPurchaseComplete();
        }
      } else {
        setError(`Payment status: ${response.status}. Please try again.`);
        setStatus('error');
      }
    } catch (err) {
      console.error('Capture error:', err);
      setError(err.response?.data?.detail || 'Payment capture failed. Please try again.');
      setStatus('error');
    }
  };

  const handleError = (err) => {
    console.error('PayPal error:', err);
    setError('Payment failed. Please try again.');
    setStatus('error');
  };

  const handleClose = () => {
    if (status !== 'processing') {
      setError(null);
      if (status !== 'success') {
        setStatus('ready');
      }
      onClose();
    }
  };

  return (
    <ModalWrapper onClose={handleClose}>
      {status === 'loading' && (
        <div className="p-8 text-center">
          <ArrowPathIcon className="w-12 h-12 text-rust animate-spin mx-auto mb-4" />
          <p className="text-sepia">Loading...</p>
        </div>
      )}

      {status === 'success' && (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-forest" />
          </div>
          <h3 className="font-display text-2xl font-bold text-ink mb-2">
            Purchase Complete!
          </h3>
          <p className="text-sepia mb-6">
            Your full yearbook is ready. Click below to download all {totalChapters} chapters.
          </p>
          <button
            onClick={() => {
              handleClose();
              if (onPurchaseComplete) {
                onPurchaseComplete();
              }
            }}
            className="btn-primary flex items-center justify-center gap-2 w-full"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            Download Full Book
          </button>
        </div>
      )}

      {status === 'error' && (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-rust/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-rust" />
          </div>
          <h3 className="font-display text-2xl font-bold text-ink mb-2">
            Payment Failed
          </h3>
          <p className="text-sepia mb-6">{error || 'Something went wrong.'}</p>
          <button
            onClick={() => {
              setError(null);
              setStatus('ready');
            }}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      )}

      {(status === 'ready' || status === 'processing') && (
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gold/20 rounded-xl flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-gold" />
            </div>
            <h3 className="font-display text-2xl font-bold text-ink">
              Get Your Full Book
            </h3>
          </div>

          <p className="text-sepia mb-4">
            Unlock all {totalChapters} chapters of your yearbook in a beautiful PDF format.
          </p>

          {/* What's included */}
          <div className="bg-parchment/30 rounded-xl p-4 mb-6">
            <h4 className="font-display font-semibold text-ink mb-3 flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-rust" />
              What's Included:
            </h4>
            <ul className="space-y-2 text-sm text-sepia">
              <li className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-forest flex-shrink-0" />
                <span>All {totalChapters} chapters with full narratives</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-forest flex-shrink-0" />
                <span>Cast of characters with AI-generated bios</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-forest flex-shrink-0" />
                <span>Beautiful PDF download (print-ready)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-forest flex-shrink-0" />
                <span>Instant digital delivery</span>
              </li>
            </ul>
          </div>

          {/* Price */}
          {priceInfo && (
            <div className="text-center mb-6">
              <p className="text-sm text-sepia mb-1">One-time purchase</p>
              <p className="text-3xl font-display font-bold text-ink">
                ${priceInfo.price} <span className="text-sm font-normal text-sepia">{priceInfo.currency}</span>
              </p>
            </div>
          )}

          {/* PayPal Buttons */}
          {status === 'processing' ? (
            <div className="text-center py-8">
              <ArrowPathIcon className="w-12 h-12 text-rust animate-spin mx-auto mb-4" />
              <p className="text-sepia">Processing your payment...</p>
            </div>
          ) : (
            <PayPalScriptProvider
              options={{
                "client-id": PAYPAL_CLIENT_ID,
                currency: priceInfo?.currency || "USD",
                intent: "capture",
              }}
            >
              <PayPalButtons
                style={{
                  layout: "vertical",
                  color: "gold",
                  shape: "rect",
                  label: "pay",
                  height: 48,
                }}
                createOrder={handleCreateOrder}
                onApprove={handleApprove}
                onError={handleError}
                onCancel={() => {
                  setStatus('ready');
                }}
              />
            </PayPalScriptProvider>
          )}

          {/* Security note */}
          <p className="mt-4 text-xs text-sepia/60 text-center">
            Secure payment powered by PayPal. Your data is encrypted.
          </p>
        </div>
      )}
    </ModalWrapper>
  );
}

/**
 * Modal wrapper component
 */
function ModalWrapper({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-book max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-sepia hover:text-ink transition-colors rounded-lg hover:bg-parchment/50 z-10"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {children}
      </div>
    </div>
  );
}

